// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/customerLifecycle/CustomerEntryGate.js

import React, { useEffect, useState } from "react";

import oasysLogo from "../../assets/oasys-logo.png";
import {
  completeCustomerProfile,
  resolveCustomerEntry,
} from "../../services/customerLifecycleApi";
import OrganizationBootstrap from "./OrganizationBootstrap";

import "./CustomerLifecycle.css";

function LifecycleShell({ eyebrow, title, description, children }) {
  return (
    <div className="customer-lifecycle-page">
      <section className="customer-lifecycle-card" aria-live="polite">
        <div className="customer-lifecycle-brand">
          <img src={oasysLogo} alt="OASYS" className="customer-lifecycle-logo" />
        </div>
        <div className="customer-lifecycle-heading">
          {eyebrow ? <p className="customer-lifecycle-eyebrow">{eyebrow}</p> : null}
          <h1>{title}</h1>
          {description ? <p>{description}</p> : null}
        </div>
        {children}
      </section>
    </div>
  );
}


async function acceptCustomerInvitation({
  apiBaseUrl,
  getAccessToken,
  invitationId,
}) {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("OASYS could not obtain your authenticated access token.");
  }

  const response = await fetch(`${apiBaseUrl}/customer-lifecycle/invitations/accept`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ invitation_id: invitationId }),
  });

  const text = await response.text();
  let payload = {};
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = {};
    }
  }

  if (!response.ok) {
    const detail = payload?.detail || {};
    throw new Error(
      detail?.message ||
        detail?.code ||
        `OASYS could not accept the invitation (${response.status}).`
    );
  }

  return payload;
}

function InvitationActivation({
  apiBaseUrl,
  getAccessToken,
  entry,
  onComplete,
  onLogout,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const invitation = entry?.invitation || {};

  const accept = async () => {
    if (!invitation?.invitation_id) {
      setError("OASYS could not identify the pending invitation.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const result = await acceptCustomerInvitation({
        apiBaseUrl,
        getAccessToken,
        invitationId: invitation.invitation_id,
      });
      if (result?.entry_state !== "existing_member") {
        throw new Error(
          "OASYS accepted the invitation but could not resolve the customer membership."
        );
      }
      onComplete?.(result);
    } catch (err) {
      console.error("Customer invitation activation failed:", err);
      setError(err?.message || "OASYS could not accept this invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LifecycleShell
      eyebrow="Invitation ready"
      title={`Join ${entry?.tenant_name || "your OASYS organization"}`}
      description="Your verified Auth0 email matches a pending OASYS invitation. Accept it to activate your organization membership and continue."
    >
      <div className="customer-lifecycle-invitation-summary">
        <div>
          <span>Organization</span>
          <strong>{entry?.tenant_name || "OASYS organization"}</strong>
        </div>
        <div>
          <span>Role</span>
          <strong>{String(invitation?.role_key || "member").replace(/_/g, " ")}</strong>
        </div>
        <div>
          <span>Verified email</span>
          <strong>{invitation?.email || "Authenticated account"}</strong>
        </div>
      </div>

      {error ? (
        <div className="customer-lifecycle-alert customer-lifecycle-alert--danger" role="alert">
          {error}
        </div>
      ) : null}

      <div className="customer-lifecycle-actions customer-lifecycle-actions--stacked">
        <button
          type="button"
          className="customer-lifecycle-primary-button"
          onClick={accept}
          disabled={submitting}
        >
          {submitting ? "Accepting invitation…" : "Accept invitation & continue"}
        </button>
        {typeof onLogout === "function" ? (
          <button
            type="button"
            className="customer-lifecycle-secondary-button"
            onClick={onLogout}
            disabled={submitting}
          >
            Use a different account
          </button>
        ) : null}
      </div>
    </LifecycleShell>
  );
}

function CustomerProfileCompletion({
  apiBaseUrl,
  getAccessToken,
  entry,
  onComplete,
  onLogout,
}) {
  const suggestedName =
    entry?.display_name &&
    String(entry.display_name).trim().toLowerCase() !== String(entry?.email || "").trim().toLowerCase()
      ? String(entry.display_name).trim()
      : "";

  const [fullName, setFullName] = useState(suggestedName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedName = fullName.trim();
    if (!normalizedName) {
      setError("Enter your full name to continue.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const result = await completeCustomerProfile({
        apiBaseUrl,
        getAccessToken,
        fullName: normalizedName,
      });

      if (
        result?.entry_state !== "existing_member" ||
        result?.profile_completion_required === true
      ) {
        throw new Error(
          "OASYS updated your profile but could not confirm identity completion. Please retry."
        );
      }

      onComplete?.(result);
    } catch (err) {
      console.error("Customer profile completion failed:", err);
      setError(err?.message || "OASYS could not complete your profile.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LifecycleShell
      eyebrow="Your account"
      title="Complete your OASYS profile"
      description="Add your name so OASYS can identify you correctly in ownership, assignments, audit history, and governed workflows."
    >
      <form className="customer-lifecycle-form" onSubmit={handleSubmit}>
        <label className="customer-lifecycle-field" htmlFor="oasys-profile-full-name">
          <span>Full name</span>
          <input
            id="oasys-profile-full-name"
            name="fullName"
            type="text"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            autoComplete="name"
            maxLength={200}
            disabled={submitting}
            autoFocus
            placeholder="Your full name"
          />
        </label>

        {entry?.email ? (
          <div className="customer-lifecycle-alert" role="status">
            Signed in as <strong>{entry.email}</strong>
          </div>
        ) : null}

        {error ? (
          <div className="customer-lifecycle-alert customer-lifecycle-alert--danger" role="alert">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          className="customer-lifecycle-primary-button"
          disabled={submitting || !fullName.trim()}
        >
          {submitting ? "Saving profile…" : "Continue"}
        </button>
      </form>

      <div className="customer-lifecycle-footnote">
        <span>Your verified account remains the authority for workspace access.</span>
        {typeof onLogout === "function" ? (
          <button type="button" className="customer-lifecycle-link-button" onClick={onLogout}>
            Use a different account
          </button>
        ) : null}
      </div>
    </LifecycleShell>
  );
}

export default function CustomerEntryGate({
  apiBaseUrl,
  getAccessToken,
  onResolved,
  onLogout,
}) {
  const [entry, setEntry] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const resolveEntry = async () => {
      setState("loading");
      setError("");

      try {
        const result = await resolveCustomerEntry({
          apiBaseUrl,
          getAccessToken,
          signal: controller.signal,
        });

        if (!active) return;

        if (result?.entry_state === "existing_member") {
          if (result?.profile_completion_required === true) {
            setEntry(result);
            setState("resolved");
            return;
          }

          onResolved?.(result);
          return;
        }

        setEntry(result || {});
        setState("resolved");
      } catch (err) {
        if (
          !active ||
          err?.name === "AbortError" ||
          err?.name === "AuthRedirectInProgress"
        ) {
          return;
        }
        console.error("Customer entry resolution failed:", err);
        setError(err?.message || "OASYS could not resolve your workspace.");
        setState("error");
      }
    };

    resolveEntry();

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, getAccessToken, onResolved, retryNonce]);

  if (state === "loading") {
    return (
      <LifecycleShell
        eyebrow="OASYS"
        title="Preparing your workspace"
        description="Resolving your authenticated OASYS account and organization access."
      >
        <div className="customer-lifecycle-progress" aria-label="Resolving workspace" />
      </LifecycleShell>
    );
  }

  if (state === "error") {
    return (
      <LifecycleShell
        eyebrow="Account access"
        title="We couldn't resolve your workspace"
        description="Your account is authenticated, but OASYS could not finish workspace resolution."
      >
        <div className="customer-lifecycle-alert customer-lifecycle-alert--danger" role="alert">
          {error}
        </div>
        <div className="customer-lifecycle-actions">
          <button
            type="button"
            className="customer-lifecycle-primary-button"
            onClick={() => setRetryNonce((value) => value + 1)}
          >
            Retry
          </button>
          {typeof onLogout === "function" ? (
            <button type="button" className="customer-lifecycle-secondary-button" onClick={onLogout}>
              Log out
            </button>
          ) : null}
        </div>
      </LifecycleShell>
    );
  }

  const entryState = String(entry?.entry_state || "").trim().toLowerCase();

  if (entryState === "existing_member" && entry?.profile_completion_required === true) {
    return (
      <CustomerProfileCompletion
        apiBaseUrl={apiBaseUrl}
        getAccessToken={getAccessToken}
        entry={entry}
        onComplete={onResolved}
        onLogout={onLogout}
      />
    );
  }

  if (entryState === "new_customer") {
    return (
      <OrganizationBootstrap
        apiBaseUrl={apiBaseUrl}
        getAccessToken={getAccessToken}
        onBootstrapComplete={onResolved}
        onLogout={onLogout}
      />
    );
  }

  if (entryState === "invitation_pending") {
    return (
      <InvitationActivation
        apiBaseUrl={apiBaseUrl}
        getAccessToken={getAccessToken}
        entry={entry}
        onComplete={onResolved}
        onLogout={onLogout}
      />
    );
  }

  if (entryState === "membership_inactive") {
    return (
      <LifecycleShell
        eyebrow="Access unavailable"
        title="Your membership is inactive"
        description="OASYS found an existing organization relationship for this account, but that membership is not currently active. Contact your organization administrator for access."
      >
        {typeof onLogout === "function" ? (
          <button type="button" className="customer-lifecycle-secondary-button" onClick={onLogout}>
            Log out
          </button>
        ) : null}
      </LifecycleShell>
    );
  }

  if (entryState === "workspace_selection_required") {
    return (
      <LifecycleShell
        eyebrow="Workspace access"
        title="Choose your OASYS workspace"
        description="This account has access to more than one OASYS workspace. Workspace selection is required before tenant-scoped services can load."
      >
        <div className="customer-lifecycle-alert" role="status">
          Your workspace relationships were resolved safely. Selection will be enabled by the dedicated workspace-entry flow.
        </div>
        {typeof onLogout === "function" ? (
          <button type="button" className="customer-lifecycle-secondary-button" onClick={onLogout}>
            Log out
          </button>
        ) : null}
      </LifecycleShell>
    );
  }

  return (
    <LifecycleShell
      eyebrow="Account access"
      title="OASYS couldn't determine your next step"
      description="The authenticated account returned an unsupported customer lifecycle state."
    >
      <div className="customer-lifecycle-alert customer-lifecycle-alert--danger" role="alert">
        Lifecycle state: {entryState || "unknown"}
      </div>
      <div className="customer-lifecycle-actions">
        <button
          type="button"
          className="customer-lifecycle-primary-button"
          onClick={() => setRetryNonce((value) => value + 1)}
        >
          Retry
        </button>
        {typeof onLogout === "function" ? (
          <button type="button" className="customer-lifecycle-secondary-button" onClick={onLogout}>
            Log out
          </button>
        ) : null}
      </div>
    </LifecycleShell>
  );
}
